import { incomeTaxYear, GST_REGISTRATION } from "@stb/jurisdiction-au/income-tax"
import type { ReactNode } from "react"

import { useLocalHref } from "@/core/i18n/locale-context"

import { displayYear } from "../../components/home/home-content"
import { formatAud, SimpleTable, SourceLine } from "../../components/landing"
import { DEFAULT_FY } from "../../components/landing/pay-calculator-model"
import { SoleTraderCalculator } from "../../components/landing/sole-trader-calculator"
import {
  answerSentence,
  DEFAULT_SOLE_FORM,
  evaluateSole,
} from "../../components/landing/sole-trader-calculator-model"
import type { Guide } from "../guide-types"
import { formatDateZh } from "./dates"

/**
 * Chinese counterpart of content/sole-trader-tax-calculator.tsx (T1321), served at
 * /zh/sole-trader-tax-calculator. Same engine, same constants, same examples: every figure comes from the rule
 * table or the engine, never from this file. Terms: docs/design/landing/copy-zh.md.
 */
const year = incomeTaxYear(DEFAULT_FY)
const fy = displayYear(DEFAULT_FY)
const checked = formatDateZh(year.resident.source.checkedOn)
const gstChecked = formatDateZh(GST_REGISTRATION.source.checkedOn)
const capChecked = formatDateZh(year.concessionalCap.source.checkedOn)

const dollars = (n: number) => `A$${n.toLocaleString("en-AU")}`
const cents = (bp: number) => `${bp / 100} 分`
const percent = (bp: number) =>
  `${new Intl.NumberFormat("en-AU", { maximumFractionDigits: 1 }).format(bp / 100)}%`
const whole = (v: Parameters<typeof formatAud>[0]) => formatAud(v).replace(/\.00$/, "")

const linkClass =
  "focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"
const h2 = "text-2xl font-semibold"
const para = "max-w-[70ch]"

/** In-page link to a page that may have a Chinese version (falls back to the English page). */
function PageLink({ href, children }: { href: string; children: ReactNode }) {
  const localHref = useLocalHref()
  return (
    <a href={localHref(href)} className={linkClass}>
      {children}
    </a>
  )
}

const brackets = year.resident.brackets
const bracketRows = brackets.map((b, i) => {
  const next = brackets[i + 1]
  const range = next
    ? `${dollars(i === 0 ? 0 : b.from + 1)} – ${dollars(next.from)}`
    : `${dollars(b.from + 1)} 及以上`
  const tax =
    b.rateBp === 0
      ? "免税"
      : b.baseDollars === 0
        ? `超出 ${dollars(b.from)} 的部分，每 A$1 征 ${cents(b.rateBp)}`
        : `${dollars(b.baseDollars)}，加上超出 ${dollars(b.from)} 的部分每 A$1 征 ${cents(b.rateBp)}`
  return { header: range, cells: [tax] }
})

/** Profit examples: the engine, resident, no wages, no HELP, Medicare levy on. */
const PROFITS = [30_000, 60_000, 90_000, 120_000, 180_000]
const profitRows = PROFITS.flatMap((profit) => {
  const o = evaluateSole({ ...DEFAULT_SOLE_FORM, income: String(profit), expenses: "0" })
  if (o.kind !== "ok") return []
  const r = o.result
  return [
    {
      header: dollars(profit),
      cells: [
        whole(r.business.total),
        formatAud(r.setAside.perMonth),
        whole(r.takeHome),
        percent(r.setAside.shareOfProfitBp),
      ],
    },
  ]
})

const startYear = Number(DEFAULT_FY.slice(0, 4))
const previousFy = displayYear(`${startYear - 1}-${String(startYear).slice(2)}`)

const FAQ_PROFIT = 80_000
const faqOutcome = evaluateSole({ ...DEFAULT_SOLE_FORM, income: String(FAQ_PROFIT), expenses: "0" })
const faqExample = faqOutcome.kind === "ok" ? faqOutcome : undefined

const example = evaluateSole(DEFAULT_SOLE_FORM)
const exampleResult = example.kind === "ok" ? example : undefined

export const soleTraderGuideZh: Guide = {
  slug: "sole-trader-tax-calculator",
  title: `澳洲个体经营者税务计算器 ${fy}`,
  description: `澳大利亚个体经营者免费税务计算器（${fy} 财年）：算出 ABN 收入每月、每季度该预留多少税款，税后能留下多少，并与受雇员工对比。仅为估算。`,
  crumb: "个体经营者税务计算器",
  h1: `个体经营者税务计算器：每月该预留多少税款（${fy}）`,
  published: "2026-11-02",
  reviewed: "2026-11-02",
  badge: "免费工具",
  financialYear: fy,
  checked,
  sources: [
    { label: "ATO，澳大利亚税务居民的税率", href: year.resident.source.url },
    { label: "ATO，GST 登记", href: GST_REGISTRATION.source.url },
  ],
  answer: answerSentence(DEFAULT_SOLE_FORM, formatAud, "zh"),
  answerMore: (
    <>
      计算器根据你的 ABN 收入和开支，再加上任何工资，按 ATO {fy}{" "}
      财年的税率，算出你的业务带来的所得税、Medicare levy（医疗保险税）和
      HELP（高等教育贷款）还款。它会显示每月或每季度该预留多少、你能留下多少，还会和收入相同的受雇员工做对比。请在下面填入你自己的数字。
    </>
  ),
  related: [
    { label: "工资计算器：受雇员工的税后工资", href: "/pay-calculator" },
    { label: "GST 计算器：给价格加上 GST 或拆出 GST", href: "/gst-calculator" },
    { label: "Cents per km 计算器：算出你的汽车开支扣除额", href: "/cents-per-km-calculator" },
    { label: "免费发票生成器：做一张 Tax Invoice", href: "/invoice-generator" },
    { label: "个体经营者开发票：发出发票，看谁已付款", href: "/invoicing" },
  ],
  toc: [
    { id: "calculator", label: "算出该预留多少" },
    { id: "how", label: "个体经营者税怎么算" },
    { id: "examples", label: "按利润看的例子" },
    { id: "rates", label: `${fy} 财年税率` },
    { id: "gst", label: `GST 与 ${dollars(GST_REGISTRATION.thresholdDollars)} 门槛` },
    { id: "super", label: "个体经营者的 super（退休金）" },
    { id: "employee", label: "ABN 还是受雇员工" },
    { id: "faq", label: "常见问题" },
  ],
  cta: {
    title: "报税前先弄清你的利润",
    description:
      "Kestly 把发票、收据和行程放在一处，到了要算该预留多少税款时，你的收入和开支已经备好。",
    ctaLabel: "免费开始",
    ctaHref: "/login?from=sole-trader-tax-calculator",
  },
  body: (
    <>
      <section aria-labelledby="calculator" className="grid gap-3 md:gap-4">
        <h2 id="calculator" className={h2}>
          算出该为税款预留多少
        </h2>
        <SoleTraderCalculator />
      </section>

      <section aria-labelledby="how" className="grid gap-3">
        <h2 id="how" className={h2}>
          个体经营者的税怎么算
        </h2>
        <p className={para}>
          个体经营者按业务利润缴所得税，利润就是业务收入减去可扣除的开支。利润会和你的其他收入（比如工资）加在一起，按与其他人相同的税率征税。Medicare
          levy 和 HELP 还款都按总额计算。
        </p>
        <p className={para}>
          客户通常会把 ABN
          发票的钱全额付给你，所以税要你自己留出来。计算器先算出工资加利润的总税额，再减去只按工资算的税额，差额就是你的业务多出来的税。除以
          12 得出每月金额，除以 4 得出每季度金额。
        </p>
        {exampleResult && (
          <p className={para}>
            举例：{whole(exampleResult.annualIncome)} 的收入和 {whole(exampleResult.expenses)}{" "}
            的开支，利润为 {whole(exampleResult.result.profit)}。税和 Medicare levy 合计{" "}
            {whole(exampleResult.result.business.total)}（{fy} 财年），所以你保留{" "}
            {whole(exampleResult.result.takeHome)}，每月需预留{" "}
            {formatAud(exampleResult.result.setAside.perMonth)}。
          </p>
        )}
        <p className={para}>
          如果 ATO 给你寄来 PAYG
          instalment（预缴税款）通知，请按通知上的金额缴纳。本计算器不计算这些金额，它显示的是全年的税。
        </p>
        <SourceLine
          sources={[{ label: "ATO，澳大利亚税务居民的税率", href: year.resident.source.url }]}
          financialYear={fy}
          checked={checked}
        />
      </section>

      <section aria-labelledby="examples" className="grid gap-3">
        <h2 id="examples" className={h2}>
          不同利润下该预留多少税款（{fy} 财年）
        </h2>
        <p className={para}>
          按澳大利亚税务居民、没有工资、没有 HELP 债务计算，已包含 Medicare
          levy。你自己的数字请用上面的计算器。
        </p>
        <SimpleTable
          caption={`${fy} 财年个体经营者各档利润对应的税款和税后所得`}
          columns={["利润", "全年税款和 Medicare levy", "每月预留", "税后所得", "占利润比例"]}
          rows={profitRows}
        />
        {year.medicare.status === "provisional" && (
          <p className="text-muted-foreground max-w-[70ch] text-sm">
            ATO 尚未公布 {fy} 财年的 Medicare levy 门槛，所以这些数字使用的是 {previousFy}{" "}
            财年的门槛。
          </p>
        )}
        <SourceLine
          sources={[
            { label: "ATO，税率", href: year.resident.source.url },
            { label: "ATO，低收入税务抵免（LITO）", href: year.lito.source.url },
            { label: "ATO，Medicare levy 减免", href: year.medicare.source.url },
          ]}
          financialYear={fy}
          checked={checked}
        />
      </section>

      <section aria-labelledby="rates" className="grid gap-3">
        <h2 id="rates" className={h2}>
          {fy} 财年所得税税率：各州相同
        </h2>
        <p className={para}>
          所得税是联邦税，所以在 NSW、VIC、QLD、WA、SA、TAS、ACT 或 NT
          做个体经营者，税率都一样。多数人还要缴 {percent(year.medicare.rateBp)} 的 Medicare
          levy；低收入者缴得少，或者不用缴。
        </p>
        <SimpleTable
          caption={`${fy} 财年居民所得税税率（不含 Medicare levy）`}
          columns={["应税收入", `这部分收入的税额（${fy} 财年）`]}
          rows={bracketRows}
        />
        <SourceLine
          sources={[{ label: "ATO", href: year.resident.source.url }]}
          financialYear={fy}
          checked={checked}
        />
      </section>

      <section aria-labelledby="gst" className="grid gap-3">
        <h2 id="gst" className={h2}>
          GST 与 {dollars(GST_REGISTRATION.thresholdDollars)} 门槛
        </h2>
        <p className={para}>
          当你的 GST（商品及服务税）营业额（扣除 GST 的业务总收入）达到{" "}
          {dollars(GST_REGISTRATION.thresholdDollars)}{" "}
          或以上，或者你刚开始做生意、预计第一年就会达到这个数额时，必须登记
          GST。出租车、豪华轿车和网约车（ATO 称
          ride-sourcing）司机不论营业额多少都必须登记。低于门槛时，登记是自愿的。
        </p>
        <p className={para}>
          计算器使用不含 GST 的收入，因为你代收的 GST 不算你的收入。给报价加上 GST 请用{" "}
          <PageLink href="/gst-calculator">GST 计算器</PageLink>。
        </p>
        <SourceLine
          sources={[{ label: "ATO，GST 登记", href: GST_REGISTRATION.source.url }]}
          checked={gstChecked}
        />
      </section>

      <section aria-labelledby="super" className="grid gap-3">
        <h2 id="super" className={h2}>
          个体经营者的 super（退休金）
        </h2>
        <p className={para}>
          计算器假设没有雇主为你的业务利润缴纳
          super，所以要不要为退休存钱由你自己决定。你申报税务扣除的个人缴费，会计入一般的税前缴费上限（concessional
          cap）：{fy} 财年是 {dollars(year.concessionalCap.dollars)}，其中也包括雇主为你的工资缴纳的
          super。计算器不包含自愿缴费，所以如果你做了自愿缴费，实际的税会更低。如果你不确定自己的工作算不算受雇、要不要缴
          super，请向 ATO 咨询。
        </p>
        <SourceLine
          sources={[{ label: "ATO，super 缴费上限", href: year.concessionalCap.source.url }]}
          financialYear={fy}
          checked={capChecked}
        />
      </section>

      <section aria-labelledby="employee" className="grid gap-3">
        <h2 id="employee" className={h2}>
          同样的收入：ABN 还是受雇员工
        </h2>
        <p className={para}>
          计算器下方的表格，把你的结果和一位拿同样金额工资的受雇员工做了对比。相同应税收入的所得税是一样的。区别在于
          ABN 要自己承担开支，而受雇员工另外还有雇主缴纳的 {percent(year.superGuarantee.rateBp)}{" "}
          super。带薪假期等雇佣权益没有计价。
        </p>
        <p className={para}>
          要比较两份工作机会，请在上面填入 ABN 的收入，把开支设为你预计的花费，再和{" "}
          <PageLink href="/pay-calculator">工资计算器</PageLink> 算出的工资结果对比。
        </p>
      </section>
    </>
  ),
  faq: [
    {
      question: "个体经营者应该为税款预留多少？",
      answer: `视利润而定。只有 ${dollars(FAQ_PROFIT)} 利润、没有其他收入的澳大利亚税务居民，${fy} 财年大约要缴 ${faqExample ? whole(faqExample.result.business.total) : "上面显示的税额"}（含 Medicare levy），占利润的 ${faqExample ? percent(faqExample.result.setAside.shareOfProfitBp) : "一定比例"}。计算器会根据你的收入、开支和工资，算出你自己的比例。`,
    },
    {
      question: "我缴的税率和受雇员工一样吗？",
      answer:
        "一样。个体经营者的利润和工资按同样的居民税率征税。不同的是，ABN 的付款通常不会预先扣税，而且计算器假设没有雇主为你的利润缴纳 super。",
    },
    {
      question: "哪些开支可以扣除？",
      answer:
        "计算器只接受一个全年的开支总额，不会判断哪些可以扣除。某项具体开支能不能扣，请咨询 ATO 或注册税务代理。Kestly 把你的收据和行程放在一处，方便留存记录。",
    },
    {
      question: "如果开支比收入还高怎么办？",
      answer:
        "计算器会显示业务没有利润、没有税，也不会把亏损抵减你的工资收入。业务亏损能不能冲减其他收入，取决于一些计算器没有套用的测试条件，请向注册税务代理确认。",
    },
    {
      question: "收入要加上 GST 吗？",
      answer: `不用。请填不含 GST 的收入。GST 营业额一旦达到 ${dollars(GST_REGISTRATION.thresholdDollars)}，你就必须登记 GST；出租车和网约车（ATO 称 ride-sourcing）司机不论营业额多少都必须登记。来源：ATO，GST 登记，核对于 ${gstChecked}。`,
    },
    {
      question: "计算器为什么要问工作周数？",
      answer:
        "按周、按天或按小时的收入，会乘以你预计全年工作的周数。默认是 52 周，如果你打算休假，请调低。",
    },
    {
      question: "计算器包含小型企业所得税抵免吗？",
      answer:
        "不包含。部分个体经营者的业务收入可以获得税务抵免（small business income tax offset）。计算器没有计入，所以你实际缴的税可能略低一些。ATO 会在你的报税表上计算。",
    },
    {
      question: "这能代替 ATO 的 PAYG instalment 通知吗？",
      answer:
        "不能。如果 ATO 给你寄来 PAYG instalment（预缴税款）通知，请按通知上的金额缴纳。计算器显示的是全年的税，不是某一期的预缴金额。",
    },
    {
      question: "这是税务建议吗？",
      answer: `不是。这是按 ATO ${fy} 财年的税率和结果下方列出的假设做出的估算。你自己的具体情况，请向 ATO 或注册税务代理核实。`,
    },
  ],
}
